import Vue, { CreateElement } from '@src/vue'
import { Component } from 'vue-property-decorator'
import { StatusOverview } from '@src/routes/members/components/status/status-overview'
import Composition from '@src/components/composition/composition'
import { TrainingFrequency } from '@src/routes/members/components/status/training-frequency'
import { DetailsAndTargets } from '@src/routes/members/components/status/details-targets'
import Member from '@src/data/member'
import { FavoriteActivities } from '@src/routes/members/components/status/favorite-activities'
import { Loader } from '@src/components/loading-screen/loader/loader'
import { HealthGoals } from '@src/routes/members/components/health-goals/health-goals'
import { Spacer } from '@src/elements/spacer/spacer'
import { formatTPDays, track } from '@src/util/functions'
import { WorkoutService } from '@src/service/workout'

import './status.sass'
import PortraitComposition from '@src/components/composition/portrait'

export class TrainingStatusComponent extends Vue {
  protected loading: boolean = true
  protected member: Member
  protected progress: { [key: string]: number } = {}
  private service: WorkoutService = new WorkoutService()

  protected async loadTrainingStatus () {
    if (this.member.currentTrainingPlan) await this.setCompositionFromTP()
    else if (this.member.activeWorkout && this.member.activeWorkout.id) {
      await this.setCompositionFromActiveWorkout(this.member.activeWorkout.id)
    }
    this.loading = false
  }

  private async setCompositionFromTP () {
    const days = await formatTPDays(this.member.currentTrainingPlan.startDate, this.member.currentTrainingPlan.trainingDays)
    // get last immutable day workout => today's workout
    let todayIndex = 0
    days.forEach((day, index) => { if (day.immutable) todayIndex = index })
    if (days[todayIndex] && days[todayIndex].workout) {
      this.progress = this.service.buildWorkoutComposition(days[todayIndex].workoutAsArray[0].workoutExercises)
    }
  }

  private async setCompositionFromActiveWorkout (workoutId) {
    return this.service.getWorkout(workoutId)
      .then(response => {
        if (response.workout && response.workout.workout_exercises) {
          const exercises = this.service.formatExercisesFromServer(response.workout.workout_exercises)
          this.progress = this.service.buildWorkoutComposition(exercises)
        }
      })
      .catch(async error => console.error('Failed to retrieve workout', error))
      .finally(() => this.loading = false)
  }
}

@Component({
  computed: {
    member () {
      return this.$store.state.selectedMember
    }
  }
})
export class MemberStatus extends TrainingStatusComponent {
  protected member: Member

  private windowWidth: number = window.innerWidth

  constructor () {
    /* istanbul ignore next: the compiled fallback of super() is unreachable, the base constructor returns the instance */
    super()
    track('/members/status')
  }

  updateWindowSize () {
    this.windowWidth = window.innerWidth
  }

  mounted () {
    window.addEventListener('resize', this.updateWindowSize)
  }

  beforeDestroy () {
    window.removeEventListener('resize', this.updateWindowSize)
  }

  updated () {
    document.title = `${this.$root.$t('PIXFORMANCE_PLATFORM')} - ${this.$root.$t('MEMBERS')} - ${this.$root.$t('PAGE.MEMBERS.DETAIL.TABS.STATUS')}`
  }

  render (h: CreateElement) {
    if (this.loading) {
      return <Loader />
    }
    let windowWidth = window.innerWidth
    return <div class='member-status-wrapper scroll-content'>
      <div>
        <HealthGoals />
        <Spacer />
        <StatusOverview member={this.member}/>
        <Spacer />
        <Composition progress={this.progress} bodyCircleDiameter={290}
                     muscleCircleDiameter={windowWidth > 800 ? 80 : 85}
                     movementCircleDiameter={windowWidth > 800 ? 80 : 85} isFilterEnabled={false}
                     title='PAGE.MEMBERS.STATUS.CURRENT_WORKOUT_OVERVIEW' showLegend={true} showAssistant={false} />
        <PortraitComposition progress={this.progress} bodyCircleDiameter={275}
                             muscleCircleDiameter={61}
                             movementCircleDiameter={this.windowWidth > 600 ? 106 : 80} isFilterEnabled={false}
                             title='PAGE.MEMBERS.STATUS.CURRENT_WORKOUT_OVERVIEW' showLegend={true} />
        <Spacer />
        <TrainingFrequency ref='trainingFrequencyChartWrapper' member={this.member} />
        <DetailsAndTargets member={this.member} />
        <Spacer />
        <FavoriteActivities member={this.member} />
      </div>
    </div>
  }

  beforeMount () {
    return this.loadTrainingStatus()
  }
}
